import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { supabase } from '../../lib/supabaseClient';
import AnnotationMap from '../operator/AnnotationMap';
import '../operator/OperatorWorkspace.css';

// Admin entry from the admin panel: the same operator annotation map, but
// framed to a single operator's polygons (?operator=<id>) or a single
// reported building (?building=<id>). The map, per-operator colours and
// hover identity are reused as-is; this only supplies initialBounds and an
// admin chrome.
const centerBox = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  fontFamily: "'Vazirmatn', sans-serif",
  fontSize: 14,
  fontWeight: 600,
  color: '#475569',
  direction: 'rtl',
};

// Pad the focused building's bbox so the admin sees ~80 m of context on each
// side. ~0.0008 deg at lat 35 is roughly 80 m N/S; we use the same number
// for lng (a touch tighter at this latitude, which is fine).
const BUILDING_BBOX_PAD_DEG = 0.0008;

export default function AdminOperatorMap() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const operatorId = params.get('operator');
  const buildingId = params.get('building');
  const [mode, setMode] = useState(null); // 'operator' | 'building' | null
  const [who, setWho] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    setReady(false);
    setBounds(null);
    setWho(null);

    if (buildingId) {
      setMode('building');
      (async () => {
        const { data } = await supabase.rpc('building_focus', { b_id: buildingId });
        if (!alive) return;
        const row = Array.isArray(data) ? data[0] : data;
        if (row && Number.isFinite(row.min_lng)) {
          setBounds({
            min_lng: row.min_lng - BUILDING_BBOX_PAD_DEG,
            min_lat: row.min_lat - BUILDING_BBOX_PAD_DEG,
            max_lng: row.max_lng + BUILDING_BBOX_PAD_DEG,
            max_lat: row.max_lat + BUILDING_BBOX_PAD_DEG,
          });
          setWho({ full_name: row.owner_name });
        }
        setReady(true);
      })();
    } else if (operatorId) {
      setMode('operator');
      (async () => {
        const [{ data: prof }, { data: bb }] = await Promise.all([
          supabase.from('profiles').select('full_name, email').eq('id', operatorId).maybeSingle(),
          supabase.rpc('operator_buildings_bbox', { op: operatorId }),
        ]);
        if (!alive) return;
        setWho(prof || null);
        const row = Array.isArray(bb) ? bb[0] : bb;
        setBounds(row && Number.isFinite(row.min_lng) ? row : null);
        setReady(true);
      })();
    } else {
      setMode(null);
      setReady(true);
    }
    return () => { alive = false; };
  }, [operatorId, buildingId]);

  const name = who?.full_name || who?.email || 'کارشناس';
  const title = mode === 'building'
    ? `ساختمانِ گزارش‌شده · ${name}`
    : `پولیگان‌های ${name}`;
  const subtitle = mode === 'building'
    ? 'نمای مدیریتی · فقط پولیگانِ گزارش‌شده'
    : 'نمای مدیریتی · فقط همین کارشناس';

  // operatorFilter narrows to one author in operator mode. In building mode we
  // instead isolate the single reported polygon via focusBuildingId, so the
  // admin sees ONLY the polygon under review and can find/act on it instantly
  // (the satellite/roadmap imagery underneath supplies the surrounding
  // context; neighbouring polygons would only get in the way).
  const operatorFilter = mode === 'operator' ? operatorId : null;
  const focusBuildingId = mode === 'building' ? buildingId : null;

  return (
    <div className="op-root">
      <header className="op-bar">
        <div className="op-bar-brand">
          <div className="op-bar-brand-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 1.6c-2.9 0-5.1 2.2-5.1 5.1 0 3.7 5.1 9.7 5.1 9.7s5.1-6 5.1-9.7C14.1 3.8 11.9 1.6 9 1.6Z" stroke="rgba(255,255,255,0.85)" strokeWidth="1.4" />
              <circle cx="9" cy="6.7" r="1.9" fill="rgba(255,255,255,0.92)" />
            </svg>
          </div>
          <span className="op-bar-title">{title}</span>
          <span className="op-bar-divider" aria-hidden="true" />
          <span className="op-bar-subtitle">{subtitle}</span>
        </div>

        <div className="op-bar-actions">
          <button className="op-logout-btn" type="button" onClick={() => navigate('/admin')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M8.75 11.667 4.083 7l4.667-4.667" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            بازگشت به پنل مدیریت
          </button>
        </div>
      </header>

      <div className="op-map">
        {mode === null && <div style={centerBox}>پارامتر نقشه (کارشناس یا ساختمان) داده نشده است.</div>}
        {mode !== null && !ready && <div style={centerBox}>در حال بارگذاری…</div>}
        {mode !== null && ready && (
          <AnnotationMap operatorFilter={operatorFilter} initialBounds={bounds} focusBuildingId={focusBuildingId} />
        )}
      </div>
    </div>
  );
}
